/**
 * A community's own groupings of its people (ADR 0052).
 *
 * A GROUPING IS THE COMMUNITY'S WORD, not the platform's. "Modded goldens"
 * means nothing outside Celeste, so there is no shared list here — only the
 * shape every community's list takes. The platform-wide vocabulary (how
 * somebody streams) is a different thing and lives elsewhere when it lands.
 *
 * The games a community is about are on the room itself (`RoomGame`, in
 * room.ts), because they are a property of the room that the room read
 * carries, not a list the admin panel edits beside it.
 */
import { z } from 'zod';
import { TwitchLogin } from './room.js';

/** A grouping's key within one community. Lowercase, so it can sit in a URL. */
export const GroupingId = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/, 'A grouping key is 1–32 lowercase letters, digits or hyphens');

/** How many groupings one community may define. A filter row, not a taxonomy. */
export const GROUPINGS_MAX = 30;

export const Grouping = z.object({
  id: GroupingId,
  label: z.string().trim().min(1).max(40),
  /**
   * One line saying what it means. Not decoration: a newcomer does not know
   * what a "golden" is, and the page's legend is built from these.
   */
  blurb: z.string().trim().max(200),
  /**
   * The grouping this one qualifies — "Vanilla" under "Speedrun". One level
   * only: a parent may not itself have a parent. Being in a child puts
   * somebody in the parent too.
   */
  parent: GroupingId.nullable(),
  /**
   * Drawn as its own block rather than mixed into the streamer list —
   * "Community events", for accounts that are organisations, not people.
   * Only editors may put anybody in one, and it has no children.
   */
  section: z.boolean(),
});
export type Grouping = z.infer<typeof Grouping>;

/** Who put somebody in a grouping. A streamer's own choice outranks an editor's. */
export const GroupingSource = z.enum(['editor', 'streamer']);

/** One person's place in the community's groupings, as the admin panel reads it. */
export const GroupingAssignment = z.object({
  login: TwitchLogin,
  groupings: z.array(z.object({ id: GroupingId, by: GroupingSource })),
  /**
   * Groupings the STREAMER took themselves out of. An editor cannot put them
   * back, and the panel shows why the box will not stay ticked.
   */
  declined: z.array(GroupingId),
});
export type GroupingAssignment = z.infer<typeof GroupingAssignment>;

/** `GET /v1/rooms/{room}/groupings`: everything, for the people who edit it. */
export const RoomGroupings = z.object({
  groupings: z.array(Grouping),
  assignments: z.array(GroupingAssignment),
});
export type RoomGroupings = z.infer<typeof RoomGroupings>;

/**
 * `PUT /v1/rooms/{room}/groupings`: the definitions and the EDITORS' entries,
 * whole.
 *
 * Whole rather than a patch per tick, because the panel is a draft that is
 * saved, like the channel list. What a streamer set for themselves is not in
 * here and is not touched by it: the server keeps those whatever this says.
 */
export const RoomGroupingsInput = z
  .object({
    groupings: z.array(Grouping).max(GROUPINGS_MAX),
    assignments: z
      .array(z.object({ login: TwitchLogin, groupings: z.array(GroupingId).max(GROUPINGS_MAX) }))
      .max(500),
  })
  .superRefine((v, ctx) => {
    const byId = new Map<string, Grouping>();
    v.groupings.forEach((g, i) => {
      if (byId.has(g.id)) {
        ctx.addIssue({ code: 'custom', path: ['groupings', i, 'id'], message: `"${g.id}" is used twice` });
      }
      byId.set(g.id, g);
    });
    v.groupings.forEach((g, i) => {
      if (g.parent === null) return;
      const parent = byId.get(g.parent);
      const why = parent === undefined
        ? `"${g.parent}" is not a grouping here`
        : parent.parent !== null
          ? 'A grouping inside a grouping cannot have its own'
          : parent.section || g.section
            ? 'A section has no groupings inside it'
            : g.parent === g.id
              ? 'A grouping cannot be inside itself'
              : null;
      if (why) ctx.addIssue({ code: 'custom', path: ['groupings', i, 'parent'], message: why });
    });
  });
export type RoomGroupingsInput = z.infer<typeof RoomGroupingsInput>;
